import React, { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import { Badge } from "../components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import { listWorkspaceFreightiq } from "../lib/api";
import { WORKSPACE } from "../constants/testIds";

export default function FreightIQ() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const rfqId = params.get("rfq_id") || undefined;
  const buyerId = params.get("buyer_id") || undefined;
  const poId = params.get("po_id") || undefined;

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const res = await listWorkspaceFreightiq({
          rfq_id: rfqId,
          buyer_id: buyerId,
          po_id: poId,
        });
        setRows(res.shipments || []);
      } catch (err) {
        toast.error(err?.response?.data?.detail || "Failed to load FreightIQ shipments");
      } finally {
        setLoading(false);
      }
    })();
  }, [rfqId, buyerId, poId]);

  return (
    <div className="min-h-screen bg-[hsl(60,5%,98%)]" data-testid={WORKSPACE.freightiqList}>
      <header className="border-b border-neutral-200 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 h-16 flex items-center justify-between">
          <Link to="/" className="inline-flex items-center gap-3">
            <span className="w-8 h-8 rounded-sm bg-[hsl(217,90%,25%)] grid place-items-center">
              <span className="text-white font-bold text-sm">D</span>
            </span>
            <span className="font-medium tracking-tight text-lg">DeMaxtore</span>
            <span className="text-xs uppercase tracking-[0.2em] font-semibold text-neutral-400 ml-4 hidden sm:inline">
              FreightIQ™️
            </span>
          </Link>
          <div className="flex items-center gap-4">
            <Link
              to="/workspace/shipments"
              className="text-xs uppercase tracking-[0.15em] font-semibold text-neutral-500 hover:text-neutral-800"
            >
              Shipments
            </Link>
            <Link
              to="/workspace/inspections"
              className="text-xs uppercase tracking-[0.15em] font-semibold text-neutral-500 hover:text-neutral-800"
            >
              Inspections
            </Link>
            <Link
              to="/workspace/purchase-orders"
              className="text-xs uppercase tracking-[0.15em] font-semibold text-neutral-500 hover:text-neutral-800"
            >
              Purchase Orders
            </Link>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 lg:px-10 py-10">
        <div className="text-xs uppercase tracking-[0.24em] font-semibold text-[hsl(217,90%,35%)]">
          Workspace
        </div>
        <h1 className="mt-3 text-3xl sm:text-4xl font-light tracking-tight">FreightIQ™️</h1>
        <p className="mt-3 text-sm text-neutral-500 max-w-2xl">
          Import shipment planning from factory pickup to destination port. Visibility after inspection
          passes — not a freight booking engine.
        </p>

        <div className="mt-10 border border-neutral-200 bg-white rounded-sm overflow-hidden">
          {loading ? (
            <div className="p-16 grid place-items-center">
              <Loader2 className="w-5 h-5 animate-spin text-[hsl(217,90%,25%)]" />
            </div>
          ) : rows.length === 0 ? (
            <div className="p-16 text-center text-sm text-neutral-500">
              No shipments yet. FreightIQ opens automatically when an inspection passes.
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="bg-[hsl(60,5%,96%)]">
                  {[
                    "Shipment ID",
                    "PO Number",
                    "Origin",
                    "Destination",
                    "Carrier",
                    "Container",
                    "ETA",
                    "Status",
                  ].map((h) => (
                    <TableHead
                      key={h}
                      className="uppercase text-[10px] tracking-[0.2em] font-semibold text-neutral-500"
                    >
                      {h}
                    </TableHead>
                  ))}
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((s) => (
                  <TableRow
                    key={s.id}
                    data-testid={WORKSPACE.freightiqRow}
                    className="cursor-pointer hover:bg-[hsl(60,5%,97%)]"
                    onClick={() => navigate(`/workspace/freightiq/${s.id}`)}
                  >
                    <TableCell className="font-mono text-xs text-[hsl(217,90%,25%)]">
                      {s.number}
                    </TableCell>
                    <TableCell className="font-mono text-xs">{s.po_number || "—"}</TableCell>
                    <TableCell className="text-sm">{s.origin || "—"}</TableCell>
                    <TableCell className="text-sm">{s.destination || "—"}</TableCell>
                    <TableCell className="text-sm">{s.carrier || s.shipping_line || "—"}</TableCell>
                    <TableCell className="text-sm">
                      {s.container_number ||
                        (s.container_type
                          ? `${s.container_quantity || 1}× ${s.container_type}`
                          : "—")}
                    </TableCell>
                    <TableCell className="text-xs text-neutral-500 whitespace-nowrap">
                      {s.eta || "—"}
                    </TableCell>
                    <TableCell>
                      <Badge className="rounded-sm bg-[hsl(217,90%,25%)] text-white uppercase text-[10px] tracking-wider">
                        {s.status}
                      </Badge>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </div>
      </main>
    </div>
  );
}
